import { useCallback, useSyncExternalStore } from 'react'

// Булево состояние вида «свёрнуто / развёрнуто», переживающее перезагрузку.
// Хранилище — localStorage, но недоступным оно бывает штатно (приватный режим,
// выключенные куки, тестовая среда без window): тогда флаг вырождается в память
// вкладки, а не роняет рендер. Значение читается из живого хранилища, поэтому
// две сетки на одном ключе видят правку друг друга без общего состояния.

const memory = new Map<string, boolean>()
const listeners = new Map<string, Set<() => void>>()

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    // Доступ к localStorage бросает при запрете хранилища — это не ошибка,
    // а режим работы: дальше живём на памяти вкладки.
    return null
  }
}

// Память вкладки — замена хранилищу, а не кэш поверх него: пока storage жив,
// правда только в нём. Иначе стёртый ключ воскресал бы из памяти вместо того,
// чтобы вернуть значение по умолчанию.
function read(key: string, fallback: boolean): boolean {
  const store = storage()
  if (!store) return memory.get(key) ?? fallback
  const raw = store.getItem(key)
  return raw === null ? fallback : raw === '1'
}

function write(key: string, value: boolean): void {
  try {
    const store = storage()
    if (store) store.setItem(key, value ? '1' : '0')
    else memory.set(key, value)
  } catch {
    // Квота или запрет записи — значение живёт в памяти вкладки.
    memory.set(key, value)
  }
  for (const notify of listeners.get(key) ?? []) notify()
}

function subscribe(key: string, onChange: () => void): () => void {
  const set = listeners.get(key) ?? new Set()
  set.add(onChange)
  listeners.set(key, set)
  // storage-событие приходит только из других вкладок — своя правка расходится
  // через listeners.
  const onStorage = (e: StorageEvent) => {
    if (e.key === key) onChange()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    set.delete(onChange)
    window.removeEventListener('storage', onStorage)
  }
}

/** Флаг под ключом хранилища: [значение, переключатель]. */
export function usePersistedFlag(
  key: string,
  fallback = false,
): [boolean, (value: boolean) => void] {
  const value = useSyncExternalStore(
    useCallback((onChange: () => void) => subscribe(key, onChange), [key]),
    useCallback(() => read(key, fallback), [key, fallback]),
    useCallback(() => fallback, [fallback]),
  )
  const set = useCallback((next: boolean) => write(key, next), [key])
  return [value, set]
}
